import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { FaTimes, FaCheck } from "react-icons/fa";
import { deleteApplciationTPE, editApplciationTPE, getAllApplciationTPE, saveApplciationTPE } from "../api/appliApi";
import { format } from "date-fns";
import fr from "date-fns/locale/fr";
import FilterComponent from "../components/FilterComponent";
import CustomLoader from "../components/LoaderComponent";
import { customHeader, paginationOptions } from "../utils/StyledDataTable";
import { Form } from "react-bootstrap";
import { FormModal } from "../components/FormModal";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import { ConfirmModal } from "../components/ConfirmModal";
import { ToastContainer, toast } from "react-toastify";
import { ConditionalStyles } from "react-data-table-component";
import { TableContainer } from "../utils/StyledComponents";
import plusCircle from "../asset/img/icon/plus-circle.svg";
import { EntityInterface } from "../utils/Type";

const clearFormData = () => ({
    id: "",
    name: "",
    active: false,
    priority: ""
});

const ApplicationTPE = () => {
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [pending, setPending] = useState(true);
    const [datas, setDatas] = useState<EntityInterface[]>([]);
    const [modalShow, setModalShow] = React.useState(false);
    const [confirmShow, setConfirmShow] = React.useState(false);
    const [selectedName, setSelectedName] = useState('');
    const [idToDelete, setIdToDelete] = useState('');
    const [formData, setFormData] = useState(clearFormData());
    const submitLockRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);

    const fetchData = async () => {
        try {
            const data = await getAllApplciationTPE();
            if (Array.isArray(data.data)) {
                setDatas(data.data);
            } else {
                setDatas([]);
            }
        } catch (error) {
            setDatas([]);
        } finally {
            setPending(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredData = useMemo(() => {
        const txt = filterText.toLowerCase();
        return datas.filter(d =>
            d.id.toString().includes(txt) ||
            d.name.toLowerCase().includes(txt)
        );
    }, [filterText, datas]);

    const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value);
    };

    const columns = [
        {
            name: 'Id',
            selector: (row: { id: string }) => row.id,
            sortable: true,
            width: '7%'
        },
        {
            name: 'Nom',
            selector: (row: { name: string }) => row.name,
            sortable: true,
            width: '40%',
        },
        {
            name: 'Favori',
            selector: (row: { priority: number }) => row.priority,
            sortable: true,
            width: '10%'
        },
        {
            name: 'Active',
            selector: (row: { active: boolean }) => row.active,
            sortable: true,
            width: '10%',
            cell: (row: { active: boolean }) => row.active ? <FaCheck /> : <FaTimes />
        },

        {
            name: 'Date de création',
            selector: (row: EntityInterface) => format(new Date(row.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
        },
        {
            name: 'Date de modification',
            selector: (row: EntityInterface) => format(new Date(row.updatedAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
        },
        {
            name: 'Action',
            cell: (row: any) => (
                <>
                    <MdEdit size={24} onClick={() => handleEdit(row)} style={{ cursor: "pointer" }} />
                    <MdDeleteForever size={24} onClick={() => handleShowConfirm(row.id, row.name)} style={{ cursor: "pointer" }} />
                </>
            ),
            ignoreRowClick: true
        }
    ];

    const conditionalRowStyles: ConditionalStyles<EntityInterface>[] = [
        {
            when: row => filteredData.indexOf(row) % 2 !== 0,
            style: { backgroundColor: "#F6F8FD" },
        },
    ];

    const handleClear = () => {
        if (filterText) {
            setResetPaginationToggle(!resetPaginationToggle);
            setFilterText('');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        })
    }

    const notify = (message: string) => toast(message);
    const notifyError = (message: string) => toast.error(message);

    const handleClearForm = () => { setFormData(clearFormData()); setSelectedName(''); };

    const handleValidate = async () => {
        if (submitLockRef.current || isSaving) return;

        submitLockRef.current = true;
        setIsSaving(true);

        try {
            if (!formData.name.trim()) {
                notifyError("Le nom est requis.");
                return;
            }

            if (formData.id) {
                await editApplciationTPE.put(formData);
            } else {
                await saveApplciationTPE.post(formData);
            }
            fetchData();
            handleClearForm();
            notify('Enregistrer avec success !');
            setModalShow(false);
        } catch (error) {
            notifyError("Erreur lors de l'enregistrement.")
        } finally {
            setIsSaving(false);
            submitLockRef.current = false;
        }
    }

    const handleEdit = (item: any) => {
        setFormData({
            id: item.id,
            name: item.name,
            active: item.active,
            priority: item.priority
        });
        setSelectedName(item.name);
        setModalShow(true);
    }

    const handleDelete = async () => {
        try {
            await deleteApplciationTPE(idToDelete);
            fetchData();
            setSelectedName('');
            setIdToDelete('');
            notify('Supprimer avec success !');
            setConfirmShow(false);
        } catch (error) {
            notifyError("Erreur lors de la suppression.")
        }
    }

    const handleShowConfirm = (id: string, name: string) => {
        setIdToDelete(id);
        setSelectedName(name);
        setConfirmShow(true);
    }
    const handleHideConfirm = () => {
        setSelectedName('');
        setIdToDelete('');
        setConfirmShow(false);
    }

    return (
        <div className="custom-content">
            <div className="custom-header">
                <span className="custom-title">Liste des Applications TPE</span>
                <button className="btn-add-circle" onClick={() => setModalShow(true)}>
                    <span className="btn-text">Ajouter une application TPE</span>
                    <span className="plus-icon-circle"><img src={plusCircle} alt="icone +" /></span>
                </button>
            </div>

            <ToastContainer />

            <FilterComponent
                filterText={filterText}
                onFilter={handleFilter}
                onClear={handleClear}
            />
            <TableContainer>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    conditionalRowStyles={conditionalRowStyles}
                    persistTableHead
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 150px)"
                    progressPending={pending}
                    progressComponent={<CustomLoader />}
                    customStyles={customHeader}
                    paginationComponentOptions={paginationOptions}
                    noDataComponent={<p style={{ marginTop: "20px" }}>Aucun enregistrement trouvé.</p>}
                />
            </TableContainer>

            <FormModal
                show={modalShow}
                onValidate={handleValidate}
                onAbort={() => {
                    if (isSaving) return;
                    setModalShow(false);
                    handleClearForm();
                }}
                title={`Détails de l'application TPE${selectedName ? ` "${selectedName}"` : ''}`}
                isBusy={isSaving}
                btnValidate={isSaving ? "Enregistrement..." : "Enregistrer"}
                abortDisabled={isSaving}
                body={
                    <>
                        <Form>
                            <Form.Group className="mb-3" controlId="tpe.ControlInput">
                                <Form.Label>Nom</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Nom"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    autoFocus
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" >
                                <Form.Label>Priorité d’affichage dans la liste (optionnel)</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Priorité"
                                    name="priority"
                                    value={formData.priority}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <div className="d-flex flex-column align-items-start gap-2">
                                <Form.Group className="d-flex align-items-start gap-4">
                                    <Form.Label className="mb-0">Active</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="switch-active"
                                        name="active"
                                        checked={formData.active}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                        </Form>
                    </>
                }
            />

            <ConfirmModal
                show={confirmShow}
                onValidate={handleDelete}
                onAbort={handleHideConfirm}
                title="Supprimer l'application TPE"
                body={"Êtes-vous sûr de vouloir supprimer l'application TPE \"" + selectedName + "\" ?"}
            />
        </div>

    );
};

export default ApplicationTPE;